// Minimal Google Calendar integration via Google Identity Services (GIS).
//
// Setup:
//   - Set VITE_GOOGLE_CLIENT_ID in web/.env to the OAuth 2.0 Web client ID
//     authorized for the Calendar API (scope:
//     https://www.googleapis.com/auth/calendar.events.readonly).
//   - The /share/* origins must be added to the authorized JavaScript
//     origins in Google Cloud Console.
//
// We use the implicit OAuth flow: a short-lived access token is fetched
// each time the user clicks "Import from calendar". No refresh token,
// no backend storage.

const SCOPE = "https://www.googleapis.com/auth/calendar.events.readonly";
const GIS_SRC = "https://accounts.google.com/gsi/client";

declare global {
  interface Window {
    google?: {
      accounts?: {
        oauth2?: {
          initTokenClient: (cfg: {
            client_id: string;
            scope: string;
            callback: (resp: { access_token?: string; error?: string }) => void;
          }) => { requestAccessToken: () => void };
        };
      };
    };
  }
}

let gisLoadPromise: Promise<void> | null = null;
function loadGis(): Promise<void> {
  if (window.google?.accounts?.oauth2) return Promise.resolve();
  if (gisLoadPromise) return gisLoadPromise;
  gisLoadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${GIS_SRC}"]`
    );
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Failed to load Google Identity Services"))
      );
      return;
    }
    const script = document.createElement("script");
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load Google Identity Services"));
    document.head.appendChild(script);
  });
  return gisLoadPromise;
}

export function isGcalConfigured(): boolean {
  return !!import.meta.env.VITE_GOOGLE_CLIENT_ID;
}

export async function getCalendarAccessToken(): Promise<string> {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
  if (!clientId) throw new Error("VITE_GOOGLE_CLIENT_ID not configured");
  await loadGis();
  return new Promise<string>((resolve, reject) => {
    const tokenClient = window.google!.accounts!.oauth2!.initTokenClient({
      client_id: clientId,
      scope: SCOPE,
      callback: (resp) => {
        if (resp.access_token) resolve(resp.access_token);
        else reject(new Error(resp.error ?? "Authorization cancelled"));
      },
    });
    tokenClient.requestAccessToken();
  });
}

export interface CalendarEvent {
  id: string;
  summary: string;
  start: string;
  end: string;
  description?: string;
  attendees: string[];
  hangoutLink?: string;
}

interface RawEvent {
  id?: string;
  summary?: string;
  description?: string;
  hangoutLink?: string;
  start?: { dateTime?: string; date?: string };
  end?: { dateTime?: string; date?: string };
  attendees?: { email?: string; displayName?: string }[];
}

export async function listTodaysEvents(
  accessToken: string
): Promise<CalendarEvent[]> {
  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);

  const url = new URL(
    "https://www.googleapis.com/calendar/v3/calendars/primary/events"
  );
  url.searchParams.set("timeMin", start.toISOString());
  url.searchParams.set("timeMax", end.toISOString());
  url.searchParams.set("singleEvents", "true");
  url.searchParams.set("orderBy", "startTime");
  url.searchParams.set("maxResults", "20");

  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) {
    throw new Error(`Calendar API error: ${res.status}`);
  }
  const json = await res.json();
  const items: RawEvent[] = json.items ?? [];
  return items.map((e) => ({
    id: e.id ?? "",
    summary: e.summary ?? "",
    start: e.start?.dateTime ?? e.start?.date ?? "",
    end: e.end?.dateTime ?? e.end?.date ?? "",
    description: e.description,
    attendees:
      e.attendees
        ?.map((a) => a.displayName ?? a.email ?? "")
        .filter((s): s is string => !!s) ?? [],
    hangoutLink: e.hangoutLink,
  }));
}

export function buildContextFromEvent(event: CalendarEvent): string {
  const parts: string[] = [];
  if (event.attendees.length > 0) {
    parts.push(`Participants : ${event.attendees.join(", ")}`);
  }
  if (event.description) {
    parts.push(event.description.trim());
  }
  return parts.join("\n").slice(0, 500);
}
